import * as React from 'react';

import {
  enumInvoiceType,
  InvoiceType,
} from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import { $columnSelectedTypes } from '~/modules/finance/_state/financeInvoices.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface TypeOption {
  id: InvoiceType;
  label: string;
}

interface InvoiceTypeColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ INVOICE TYPE COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const InvoiceTypeColumnFilter: React.FC<
  InvoiceTypeColumnFilterProps
> = ({ onClose }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [selectedTypesSet, setSelectedTypesSet] = useAtom($columnSelectedTypes);

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Memoized values
  const selectedTypes = React.useMemo(
    () => Array.from(selectedTypesSet),
    [selectedTypesSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedTypes),
    [selectedTypes],
  );

  const typeOptions: TypeOption[] = React.useMemo(
    () =>
      Object.values(enumInvoiceType).map((type) => ({
        id: type,
        label: t.enums.invoiceType[type] ?? type,
      })),
    [t.enums.invoiceType],
  );

  const filteredTypeOptions = React.useMemo(
    () =>
      typeOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [typeOptions, searchQuery],
  );

  const selectedTypeOptions = React.useMemo(
    () => typeOptions.filter((option) => selectedTypes.includes(option.id)),
    [typeOptions, selectedTypes],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedTypesSet(new Set(Array.from(keys) as InvoiceType[]));
    },
    [setSelectedTypesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedTypesSet(new Set());
  }, [setSelectedTypesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: TypeOption, isSelected: boolean) => (
      <FilterLabel label={item.label} isSelected={isSelected} />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@InvoiceTypeColumnFilter min-w-[250px]"
      entries={filteredTypeOptions}
      selectedEntries={selectedTypeOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(type) => type.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.invoices.list.search.placeholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
